import { Fragment, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Dialog, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import { Edit } from '@mui/icons-material'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { Group } from '../types/models'

/** Same rule as the server (`UserGroup.normalize_name/1`): at least 3 letters or digits. */
export function isValidGroupName(name: string): boolean {
  return (name.match(/[\p{L}\p{N}]/gu) ?? []).length >= 3
}

interface Props {
  group: Group
  onRenamed: (name: string) => void
}

/**
 * Rename button for a group the caller can administer, with a dialog prefilled with the current
 * name. Saves through `PATCH /user-groups/:id` with `{name}`; permanent groups are not offered.
 */
export function RenameGroupButton({ group, onRenamed }: Props): React.ReactElement | null {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const { t } = useTranslation()
  const app = useApp()

  if (!group.id || !group.editable || group.permanent) {
    return null
  }

  const trimmed = name.trim()
  const valid = isValidGroupName(trimmed)

  const onOpen = () => {
    setName(group.name)
    setOpen(true)
  }

  const onSave = () => {
    if (!valid) {
      return
    }
    setSaving(true)
    callAxios({ method: 'PATCH', url: `/user-groups/${group.id}` }, { name: trimmed })
      .then(res => {
        setSaving(false)
        if (res.success) {
          setOpen(false)
          app.addInfo(t('Group renamed.'))
          onRenamed(trimmed)
        } else {
          app.addError(res)
        }
      })
  }

  return (
    <Fragment>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button data-cy="button-Rename group" variant="outlined" color="secondary" size="large"
          onClick={onOpen} endIcon={<Edit/>}>
          <Trans>Rename group</Trans>
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle sx={{ backgroundColor: 'blue' }}>
          {t('Rename group {name}').replace('{name}', group.name)}
        </DialogTitle>
        <Box component="form" sx={{ p: 2 }} onSubmit={(e: React.FormEvent) => { e.preventDefault(); onSave() }}>
          <TextField fullWidth autoFocus label={t('Name')} value={name}
            inputProps={{ 'data-cy': 'input-group-name' }}
            error={!valid} onChange={e => setName(e.target.value)}/>
          {!valid &&
            <Typography data-cy="rename-group-empty" variant="body2" color="error" sx={{ mt: 1 }}>
              {t('BAD_GROUP_NAME')}
            </Typography>}
          <Stack direction="row" spacing={2} sx={{ m: 2 }}>
            <Button color="primary" onClick={() => setOpen(false)}><Trans>Cancel</Trans></Button>
            <Button color="secondary" type="submit" data-cy="button-Rename group OK"
              disabled={!valid || saving}>
              <Trans>Save</Trans>
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Fragment>
  )
}
